import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, GraduationCap, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useStreak } from "@/context/StreakContext";

// The badge the member is closest to finishing, so the card always has something to aim at.
const nextBadge = (badges) => {
  const open = badges.filter((b) => !b.earned);
  if (!open.length) return null;
  return open.sort(
    (a, b) => b.have / b.goal - a.have / a.goal || a.goal - a.have - (b.goal - b.have),
  )[0];
};

export const PracticeCard = () => {
  const { streak } = useStreak();
  const [badge, setBadge] = useState(null);
  const [allDone, setAllDone] = useState(false);

  useEffect(() => {
    api
      .get("/badges/mine")
      .then(({ data }) => {
        const list = data.badges || [];
        const next = nextBadge(list);
        setBadge(next);
        setAllDone(list.length > 0 && !next);
      })
      .catch((e) => console.error("could not read the badges", e));
  }, []);

  const days = streak?.current ?? 0;
  const left = badge ? Math.max(badge.goal - badge.have, 0) : 0;

  return (
    <Link
      to="/progress"
      data-testid="support-practice-card"
      className="group mt-10 block rounded-2xl border border-white/10 bg-panel p-5 transition-colors hover:border-brand/50"
    >
      <div className="flex items-center gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/15 text-brand transition-transform duration-300 group-hover:scale-105">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="font-display text-base font-bold tracking-tight">Practice</p>
          <p className="mt-0.5 text-sm text-ink-soft">
            Your progress, drills, quizzes and badge wall — all in one place.
          </p>
        </div>
        <span
          className="ms-auto hidden shrink-0 text-sm font-semibold text-brand sm:block"
          data-testid="support-goto-practice"
        >
          Open →
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="practice-snapshot">
        <span
          data-testid="practice-snapshot-streak"
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-sm"
        >
          <Flame className="h-3.5 w-3.5 text-amber-400" />
          {days > 0 ? (
            <span>
              <b>{days}</b> day{days === 1 ? "" : "s"} in a row
            </span>
          ) : (
            <span className="text-ink-soft">Start a streak today</span>
          )}
        </span>

        {badge && (
          <span
            data-testid="practice-snapshot-badge"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-surfacealt px-3 py-1.5 text-sm text-ink-soft"
          >
            <Trophy className="h-3.5 w-3.5 text-brand" />
            <span>
              Next badge: <b className="text-ink">{badge.name}</b> · {left} to go
            </span>
          </span>
        )}

        {allDone && (
          <span
            data-testid="practice-snapshot-badge"
            className="inline-flex items-center gap-1.5 rounded-full border border-forest/30 bg-forest/10 px-3 py-1.5 text-sm"
          >
            <Trophy className="h-3.5 w-3.5 text-forest" />
            Every badge earned
          </span>
        )}
      </div>

      {badge && (
        <div className="mt-3 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-white/10">
          <div
            data-testid="practice-snapshot-bar"
            className="h-full rounded-full bg-brand transition-[width] duration-500"
            style={{ width: `${Math.round((badge.have / badge.goal) * 100)}%` }}
          />
        </div>
      )}
    </Link>
  );
};
